import { Box, Flex } from '@/components/atoms/Grid'
import { FC, useContext, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import 'rc-slider/assets/index.css'
import RangeSliderRedesign from '@/components/molecules/rangeSliderRedesign'
import { Capitalized } from '@/components/atoms/typography'
import TextInput from '@/components/molecules/textInput'
import useTranslation from 'next-translate/useTranslation'
import {
  useFilter,
  useActiveFilterDispatch
} from '@/providers/filterProvider/filtersProvider'
import { FilterQueryItem } from 'types/filterQueryItem'
import { CurrencyContext } from '@/providers/currencyProvider'
import { PriceRange } from '@/network/graphql.g'
import { ActiveFiltersPriceType } from 'types/activeFiltersType'
import {
  CloseIconButton,
  ConfirmButtonRedesign,
  ResetButtonRedesign
} from '@/components/atoms/button'
import { validFor } from '@/theme/validFor'

type PriceFilterRedesignProps = {
  priceRange: PriceRange
  onAfterChange?: (PriceRangeModel) => void
  onClose?: () => void
} & Rest

enum TextInputType {
  min,
  max
}

const PriceFilterRedesign: FC<PriceFilterRedesignProps> = ({
  priceRange: defaultPriceRange,
  onClose,
  ...rest
}) => {
  const currency = useContext(CurrencyContext)
  const { t } = useTranslation('common')
  const { activeFilters } = useFilter()
  const { toggleFilterAndSetUrl } = useActiveFilterDispatch()
  const [priceRange, setPriceRange] = useState<PriceRange>(defaultPriceRange)
  useEffect(() => {
    setPriceRange({
      from:
        activeFilters.price?.query?.min || activeFilters.price?.original?.min,
      to: activeFilters.price?.query?.max || activeFilters.price?.original?.max
    })
  }, [activeFilters.price])
  const handleNewValues = (newMin: number, newMax: number): PriceRange => {
    const boundedMax = Math.min(newMax, defaultPriceRange.to)
    const newValue = {
      from: Math.min(newMin, priceRange.to),
      to: Math.max(priceRange.from, boundedMax)
    }
    return newValue
  }
  const handleOnSliderChange = (rangeArray: number[]) => {
    const [min, max] = rangeArray
    const newPriceRangeModel = handleNewValues(min, max)
    setPriceRange(newPriceRangeModel)
  }
  const handleOnBlur = (inputType: TextInputType) => (text: string) => {
    const newValue = parseFloat(text.replace(',', '.'))
    let newPriceRangeModel: PriceRange
    switch (inputType) {
      case TextInputType.max:
        newPriceRangeModel = handleNewValues(
          priceRange.from,
          isNaN(newValue) ? priceRange.to : newValue
        )
        break
      case TextInputType.min:
        newPriceRangeModel = handleNewValues(
          isNaN(newValue) ? priceRange.from : newValue,
          priceRange.to
        )
        break
    }
    setPriceRange(newPriceRangeModel)
    sendPriceRange(newPriceRangeModel)
  }
  const sendPriceRange = (priceRange: PriceRange) => {
    const newPriceRange: ActiveFiltersPriceType = {
      min: priceRange.from <= defaultPriceRange.from ? null : priceRange.from,
      max: priceRange.to >= defaultPriceRange.to ? null : priceRange.to
    }
    toggleFilterAndSetUrl(newPriceRange, '', FilterQueryItem.PRICE_MAX)
  }

  const handleResetPrice = () => {
    setPriceRange(defaultPriceRange)
    sendPriceRange(defaultPriceRange)
  }

  const handlePriceOnClick = () => {
    sendPriceRange(priceRange)
    if (onClose) {
      onClose()
    }
  }

  return (
    <Box {...rest}>
      <StyledFlex>
        <StyledCapitalized>{t('Category.Filter.price')}</StyledCapitalized>
        <CloseIconButton
          onClick={onClose}
          width={10}
          height={10}
          showBackground={false}
        />
      </StyledFlex>
      <Flex flexColumnGap={10} my={14} justifyContent="space-between" px={24}>
        <Box>
          <PriceInfoText>{t('Category.Filter.Price.cheapest')}</PriceInfoText>
          <PriceBox>
            {currency.leftSymbol && (
              <Capitalized m={0} pl={12}>
                {currency.leftSymbol}
              </Capitalized>
            )}
            <StyledTextInput
              onBlur={handleOnBlur(TextInputType.min)}
              hideBorder={true}
              placeholder={''}
              value={priceRange?.from?.toString()}
              data-testid="priceFrom"
            />
            {currency.rightSymbol && (
              <StyledCapitalized2 m={0} pr={12}>
                {currency.rightSymbol}
              </StyledCapitalized2>
            )}
          </PriceBox>
        </Box>
        <Box>
          <PriceInfoText2>
            {t('Category.Filter.Price.mostExpensive')}
          </PriceInfoText2>
          <PriceBox>
            {currency.leftSymbol && (
              <Capitalized m={0} pl={12}>
                {currency.leftSymbol}
              </Capitalized>
            )}
            <StyledTextInput
              onBlur={handleOnBlur(TextInputType.max)}
              hideBorder={true}
              placeholder={''}
              value={priceRange?.to?.toString()}
              data-testid="priceTo"
            />
            {currency.rightSymbol && (
              <StyledCapitalized2 m={0} pr="6px">
                {currency.rightSymbol}
              </StyledCapitalized2>
            )}
          </PriceBox>
        </Box>
      </Flex>
      <RangeSliderBox>
        <RangeSliderRedesign
          min={defaultPriceRange.from}
          max={defaultPriceRange.to}
          value={[priceRange.from, priceRange.to]}
          onChange={handleOnSliderChange}
          onAfterChange={() => sendPriceRange(priceRange)}
        />
      </RangeSliderBox>
      <ButtonsWrapper>
        <ConfirmButtonRedesign
          title={t('Category.Filter.confirmSelection')}
          onClick={handlePriceOnClick}
          data-testid="confirmFilterButton"
        />
        <ResetButtonRedesign
          title={t('Category.Filter.resetFilters')}
          onClick={handleResetPrice}
          data-testid="resetPriceButton"
        />
      </ButtonsWrapper>
    </Box>
  )
}

export default PriceFilterRedesign

const PriceInfoTextProps = css`
  margin-bottom: 10px;
  margin-top: 0;
  font-weight: 500;
  font-size: 10px;
  color: #757575;
  ${validFor.mobile`
    margin-bottom: 4px;
  `}
`

const StyledFlex = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  padding: 5px 12px;
  background-color: ${(props) => props.theme.colors.navMenuGrey};
  gap: 16px;
  ${validFor.mobile`  
  display: none;
  `}
  ${validFor.tablet`  
  display: none;
  `}
`

const StyledCapitalized = styled(Capitalized)`
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  margin: 0;
`

const PriceBox = styled(Flex)`
  justify-content: space-between;
  width: 64px;
  height: 24px;
  box-sizing: border-box;
  align-items: center;
  border: 0.5px solid ${(props) => props.theme.colors.buttonColorNew};
  border-radius: 2px;
  ${validFor.mobile`
    height: 35px;
    width: 80px;
  `}
`

const PriceInfoText = styled(Capitalized)`
  ${PriceInfoTextProps}
`

const PriceInfoText2 = styled(Capitalized)`
  ${PriceInfoTextProps}
  text-align: right;
`

const StyledTextInput = styled(TextInput)`
  font-size: 12px;
  padding: 4px 4px 4px 12px;
  border-radius: 2px;
  ${validFor.mobile`
    font-size: 16px;
    font-weight: 500;
    text-align: right;
      padding-left: 2px;
  `}
`

const StyledCapitalized2 = styled(Capitalized)`
  font-weight: 500;
  font-size: 12px;
  margin: 0;
  ${validFor.mobile`
    font-size: 16px;
    font-weight: 500;
    margin-right: 4px;
  `}
`

const ButtonsWrapper = styled(Flex)`
  padding: 8px;
  border-top: 1px solid ${(props) => props.theme.colors.navMenuGrey};
  flex-direction: column;
  gap: 4px;
  ${validFor.mobile`
  display: none;  
  `}
  ${validFor.tablet`
  display: none;  
  `}
`

const RangeSliderBox = styled(Box)`
  width: calc(100% - 48px);
  justify-self: center;
  margin-bottom: 20px;
  ${validFor.mobile`
    margin: 0 auto;
  `}
`
